import React, { useState, useEffect } from 'react'
import { Form, Input, Select, Popover } from 'antd'
import styles from './MerchantJoin.module.scss'
import Header from '../merchantPc/components/Header'
import { withTranslator } from '@common/hoc/translatorHoc'
import { WithI18nProps } from '@/types/i18n'
import { createMerchantI18n } from '@/utils/i18n'
import fetchRequest from '@utils/fetchRequest'
import CustomButton from '@/common/components/CustomButton'
import { isProd } from '@/utils/utils'
import { useDeviceType } from '@/common/hoc/getDeviceType'
import dianpingQR from '@assets/img/dianpingQR.png'
import myStoreIcon from '@assets/img/myStore.png'
import successIcon from '@assets/img/success.png'
import errorIcon from '@assets/img/error.png'


const { Option } = Select
const { TextArea } = Input

interface MerchantJoinFormValues {
  contactName: string
  phonePrefix: string
  contactPhone: string
  contactEmail: string
  companyName: string
  companyAddress: string
  country: string[]
  shopCount: string
  remark?: string
}

const MerchantJoin: React.FC<WithI18nProps> = (props) => {
  const { i18n } = props
  const merchantT = React.useMemo(() => createMerchantI18n(i18n), [i18n])
  const [form] = Form.useForm()
  const [isModalOpen, setIsModalOpen] = useState(false)
  // 判断是成功还是失败
  const [isSuccess, setIsSuccess] = useState(true)
  const [loading, setLoading] = useState(false)
  const deviceType = useDeviceType()

   // 把页面标签名 改为Meituan Merchant
   useEffect(() => {
    document.title = 'Meituan Merchant'
  }, [])

  useEffect(() => {
    if (deviceType === 'mobile') {
      window.location.href = isProd() ? 'https://osm.dianping.com/os/h5/seek-investment/merchantH5Join.html' : 'https://osm.51ping.com/os/h5/seek-investment/merchantH5Join.html', '_blank'
    }
  }, [deviceType])

  const onFinish = async (values: MerchantJoinFormValues) => {
    if (loading) return
    setLoading(true)
    try {
      const payload = {
        ...values,
        contactPhone: values.phonePrefix ? `${values.phonePrefix}-${values.contactPhone}` : values.contactPhone,
        country: values.country.join(','),
        remark: values.remark || ''
      }

      const res = await fetchRequest.post('/overseas/merchant/agent/site/submit', payload, {
        params: { formType: 1 }
      })
      if (+res.code === 200) {
        setIsModalOpen(true)
        setIsSuccess(true)
      } else {
        // message.error(res.msg || merchantT('failedDescription', '提交失败，请稍后再试') || 'Submission failed, please try again later')
        setIsModalOpen(true)
        setIsSuccess(false)
      }
    } catch (error) {
      console.error('Submission failed:', error)
      // message.error(merchantT('failedDescription', '提交失败，请稍后再试') || 'Submission failed, please try again later')
      setIsModalOpen(true)
      setIsSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    window.location.href = isProd() ? 'https://osm.dianping.com/os/h5/seek-investment/merchantPc.html' : 'https://osm.51ping.com/os/h5/seek-investment/merchantPc.html'
  }

  const popoverContent = (
      <div className={styles.popoverContent}>
      <div className={styles.popoverContentItem}>
        <div className={styles.popoverContentItemTitle}>{merchantT('joinUsStep1', '1.下载大众点评app')}</div>
        <div className={styles.popoverContentItemContent}>
          <img src={dianpingQR} alt="Dianping QR Code" />
        </div>
      </div>
      <div className={styles.popoverContentItem}>
        <div className={styles.popoverContentItemTitle}>{merchantT('joinUsStep2', '2.打开大众点评app，进入我的-更多-我的店铺-免费认领门店')}</div>
        <div className={styles.popoverContentItemContent}>
          <img src={myStoreIcon} alt="My Store" />
        </div>
      </div>
    </div>
  )

  return (
    <div className={styles.container}>
      <Header onJoin={() => { }} isJoinPage={true} />

      <main className={styles.content}>
        <div className={styles.title}>{merchantT('joinUs', '我要入驻')}</div>
        <div className={styles.tips}>
          {merchantT('joinUsDescription', '您可以进入大众点评app免费认领您的门店，点此')}

          <Popover content={popoverContent} trigger="hover" placement="bottom">
            <span className={styles.tipsLink}>{merchantT('joinUsSpecialDescription', '查看详情')}</span>
          </Popover>
        </div>

        <Form
          form={form}
          layout="vertical"
          className={styles.form}
          onFinish={onFinish}
          autoComplete="off"
          key={merchantT('operatingArea', '国家/区域')}
        >
          {/* Intended cooperation Area */}
          <Form.Item
            label={merchantT('operatingArea', '国家/区域')}
            name="country"
            rules={[{ required: true, message: merchantT('operatingAreaPlaceholder', '请选择国家/区域') }]}
          >
            <Select placeholder={merchantT('operatingAreaPlaceholder', '请选择国家/区域')} mode="multiple" allowClear>
              <Option value={merchantT('HongKong', '中国香港')}>{merchantT('HongKong', '中国香港')}</Option>
              <Option value={merchantT('Macau', '中国澳门')}>{merchantT('Macau', '中国澳门')}</Option>
              <Option value={merchantT('Japan', '日本')}>{merchantT('Japan', '日本')}</Option>
              <Option value={merchantT('Korea', '韩国')}>{merchantT('Korea', '韩国')}</Option>
              <Option value={merchantT('Singapore', '新加坡')}>{merchantT('Singapore', '新加坡')}</Option>
              <Option value={merchantT('Thailand', '泰国')}>{merchantT('Thailand', '泰国')}</Option>
              <Option value={merchantT('Malaysia', '马来西亚')}>{merchantT('Malaysia', '马来西亚')}</Option>
              <Option value={merchantT('Vietnam', '越南')}>{merchantT('Vietnam', '越南')}</Option>
              <Option value={merchantT('other', '其他')}>{merchantT('other', '其他')}</Option>
            </Select>
          </Form.Item>

          {/* storeBrandName */}
          <Form.Item
            label={merchantT('storeBrandName', '店铺/品牌名称')}
            name="shopName"
            rules={[{ required: true, message: merchantT('storeBrandNamePlaceholder', '请输入店铺/品牌名称') }]}
          >
            <Input placeholder={merchantT('storeBrandNamePlaceholder', '请输入店铺/品牌名称')} maxLength={200} showCount />
          </Form.Item>
          
          {/* Phone Number */}
          <Form.Item label={merchantT('phoneNumber', '联系电话')} required >
            <div className={styles.phoneItem}>
              <Form.Item name="phonePrefix" initialValue="+86" noStyle>
                <Select className={styles.phonePrefixSelect} popupMatchSelectWidth={false} >
                  <Option value="+86">+86 {merchantT('China', '中国')}</Option>
                  <Option value="+852">+852 {merchantT('HongKong', '中国香港')}</Option>
                  <Option value="+853">+853 {merchantT('Macau', '中国澳门')}</Option>
                  <Option value="+81">+81 {merchantT('Japan', '日本')}</Option>
                  <Option value="+82">+82 {merchantT('Korea', '韩国')}</Option>
                  <Option value="+65">+65 {merchantT('Singapore', '新加坡')}</Option>
                  <Option value="+66">+66 {merchantT('Thailand', '泰国')}</Option>
                  <Option value="+60">+60 {merchantT('Malaysia', '马来西亚')}</Option>
                  <Option value="+84">+84 {merchantT('Vietnam', '越南')}</Option>
                  <Option value="">{merchantT('other', '其他')}</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="contactPhone"
                rules={[{ required: true, message: merchantT('phoneNumberPlaceholder', '请输入联系电话') }]}
                style={{ flex: 1 }}
                noStyle
              >
                <Input style={{ flex: 1 }} placeholder={merchantT('phoneNumberPlaceholder', '请输入联系电话')} maxLength={20} showCount />
              </Form.Item>
            </div>
          </Form.Item>

          {/* Email */}
          <Form.Item
            label={merchantT('email', '联系邮箱')}
            name="contactEmail"
            rules={[
              { required: true, message: merchantT('emailPlaceholder', '请输入联系邮箱') },
            ]}
          >
            <Input placeholder={merchantT('emailPlaceholder', '请输入联系邮箱')} maxLength={50} showCount />
          </Form.Item>

          {/* contactPosition */}
          <Form.Item
            label={merchantT('contactPosition', '联系人职位')}
            name="contactPosition"
            rules={[{ required: true, message: merchantT('contactPositionPlaceholder', '请选择联系人职位') }]}
          >
            <Select placeholder={merchantT('contactPositionPlaceholder', '请选择联系人职位')} >
              <Option value={merchantT('brandOwner', '品牌负责人')}>{merchantT('brandOwner', '品牌负责人')}</Option>
              <Option value={merchantT('shopOwner', '门店老板')}>{merchantT('shopOwner', '门店老板')}</Option>
              <Option value={merchantT('shopManager', '店长')}>{merchantT('shopManager', '店长')}</Option>
              <Option value={merchantT('other', '其他')}>{merchantT('other', '其他')}</Option>
            </Select>
          </Form.Item>

          {/* Contact Name */}
          <Form.Item
            label={merchantT('contactName', '联系人姓名')}
            name="contactName"
            rules={[{ required: true, message: merchantT('contactNamePlaceholder', '请输入联系人姓名') }]}
          >
            <Input placeholder={merchantT('contactNamePlaceholder', '请输入联系人姓名')} maxLength={100} showCount />
          </Form.Item>
          {/* Supplementary Notes */}
          <Form.Item
            label={merchantT('supplementaryNotes', '补充说明')}
            name="remark"
          >
            <TextArea rows={4} placeholder={merchantT('supplementaryNotesPlaceholder', '可填写其他需要补充说明的信息')} maxLength={500} showCount />
          </Form.Item>

          <Form.Item>
            <CustomButton onClick={() => form.submit()} className={styles.submitBtn}>
              {merchantT('submit', '提交')}
            </CustomButton>
          </Form.Item>
        </Form>
      </main>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleModalClose}>
          <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalSuccessIcon}>
              <img src={isSuccess ? successIcon : errorIcon} alt="Success" />
            </div>
            <div className={styles.modalTitle}>{isSuccess ? merchantT('success', '提交成功') : merchantT('failed', '提交失败')}</div>
            <div className={styles.modalBody}>
              {isSuccess ? merchantT('submitDescription', '我们已收到您提交的合作意向，我们会在5-10个工作日内和您取得联系，感谢您对我们的信任！') : merchantT('failedDescription', '提交失败，请稍后再试')}
            </div>
            <div className={styles.modalButtonWrapper}>
              <button className={styles.modalConfirmButton} onClick={handleModalClose}>
                {merchantT('confirm', '确认')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const MerchantJoinWithTranslator = withTranslator(MerchantJoin)
export default MerchantJoinWithTranslator
